import { Option } from '~/components/Select';
import { ADDRESS_INPUT_FIELDS } from '~/constants/addressBook';
import countries, {
  countriesWithoutZip,
  Country,
  CountryIso2,
  uspsTrackingCountryAdditionalWhitelist,
  uspsTrackingCountryIso2Whitelist,
} from '~/constants/countries';
import states, { usMilitaryCities, usMilitaryStates, usTerritoryStates } from '~/constants/states';
import isEqual from 'lodash-es/isEqual';
import * as moment from 'moment';
import { StoreName } from '~/types/Store';
import { formatCountryToOption } from '~/utils/field-formaters';
import APIAddress from './APIAddress';
import Order from './Order';
import { BatchEntryExtended } from './OrderBatchEntry';

type AddressToType = 'to';
type AddressSenderType = 'sender';
type AddressReturnType = 'return';
type AddressType = AddressToType | AddressSenderType | AddressReturnType;

type State = {
  name: string;
  iso: string;
};

interface JSONBaseAddress {
  name: string;
  company: string;
  email: string;
  phone: string;
  street1: string;
  street2: Maybe<string>;
  street_no: Maybe<string>;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export enum AddressTypeV2 {
  RESIDENTIAL = 'residential',
  COMMERCIAL = 'commercial',
  UNKNOWN = 'unknown',
}

export interface JSONAddressForAddressBook {
  name: string;
  organization: string;
  email: string;
  phone: string;
  address_line_1: string;
  address_line_2: Maybe<string>;
  street_no: Maybe<string>;
  city_locality: string;
  state_province: string;
  postal_code: string;
  country_code: CountryIso2;
  address_type: AddressTypeV2;
}

interface JSONAddressBookAddress {
  id: string;
  address: JSONAddressForAddressBook;
  updated_at: string;
  created_at: string;
}

interface JSONAddress extends JSONBaseAddress {
  id: number;
  object_state: string;
  object_purpose: string;
  object_source: string;
  object_created: string;
  object_updated: string;
  object_id: string;
  object_owner: string;
  name: string;
  company: string;
  street_no: Maybe<string>;
  street1: string;
  street2: Maybe<string>;
  street3: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  longitude?: Maybe<string>;
  latitude?: Maybe<string>;
  phone: string;
  email: string;
  is_residential: boolean | null;
  ip: Maybe<string>;
  messages: string[];
  metadata: string;
  test: boolean;
}

interface DisplayInvoiceAddress {
  company: string;
  email: string;
  street1: string;
  street2?: string;
  city: string;
  state?: string;
  zip: string;
  country: string;
}

interface BaseAddress {
  name: string;
  company: string;
  email: string;
  phone: Maybe<string>;
  street1: string;
  street2: Maybe<string>;
  streetNumber: Maybe<string>;
  city: string;
  state: string;
  zip: string;
  country: Maybe<Country>;
  isResidential?: boolean | null;
  objectId?: string;
}

interface Address extends BaseAddress {
  id: number;
  objectId: string;
  objectState: string;
  objectPurpose: string;
  objectSource: string;
  objectCreated: Date;
  objectUpdated: Date;
  objectOwner: string;
  streetNumber: Maybe<string>;
  street3: string;
  longitude?: Maybe<string>;
  latitude?: Maybe<string>;
  isResidential: boolean | null;
  ip: Maybe<string>;
  messages: string[];
  metadata: string;
  test: boolean;
}

interface AddressIt {
  number: string;
  parts: string[];
  postalcode: string;
  regions: string[];
  state: string;
  street: string;
  text: string;
  unit: string;
  country: string;
}

interface PastedAddress {
  BuildingNumber: string;
  BuildingName?: string;
  Street: string;
  SubBuilding: string;
  City: string;
  PostalCode: string;
  ProvinceCode: string;
  ProvinceName: string;
  CountryName: string;
  CountryIso2: CountryIso2;
  Line1?: string;
  Line2?: string;
}

export type WebAddress = {
  city: string;
  company: string;
  country: CountryIso2;
  email: string;
  name: string;
  phone: string;
  state: string;
  street1: string;
  street2: string;
  street3: string;
  street_no: string;
  zip: string;
  ip: string;
  is_residential: boolean;
  latitude: string;
  longitude: string;
  metadata: string;
  object_created: Date;
  object_id: string;
  object_purpose: 'PURCHASE' | 'QUOTE';
  object_source: string;
  object_state: string;
  object_updated: Date;
};

function fromJSON(jsonAddress: JSONAddress): Maybe<Address> {
  if (!jsonAddress) return null;

  return {
    id: jsonAddress.id,
    objectId: jsonAddress.object_id,
    objectState: jsonAddress.object_state,
    objectPurpose: jsonAddress.object_purpose,
    objectSource: jsonAddress.object_source,
    objectCreated: new Date(jsonAddress.object_created),
    objectUpdated: new Date(jsonAddress.object_updated),
    objectOwner: jsonAddress.object_owner,
    name: jsonAddress.name,
    company: jsonAddress.company,
    streetNumber: jsonAddress.street_no,
    street1: jsonAddress.street1,
    street2: jsonAddress.street2,
    street3: jsonAddress.street3,
    city: jsonAddress.city,
    state: jsonAddress.state,
    zip: jsonAddress.zip,
    country: countries[jsonAddress.country as keyof typeof countries],
    longitude: jsonAddress.longitude,
    latitude: jsonAddress.latitude,
    phone: jsonAddress.phone,
    email: jsonAddress.email,
    isResidential: jsonAddress.is_residential,
    ip: jsonAddress.ip,
    messages: jsonAddress.messages,
    metadata: jsonAddress.metadata,
    test: jsonAddress.test,
  };
}

/**
 * This is a mapper function to map JSONAddressBookAddress to BaseAddress
 * This is used to parse the Address book search API response and feed it into
 * the existing address forms
 * @param jsonAddress
 * @returns
 */
function fromAddressBookJSON(jsonAddress: JSONAddressBookAddress): Maybe<BaseAddress> {
  if (!jsonAddress) return null;
  return {
    objectId: jsonAddress.id,
    name: jsonAddress.address.name,
    company: jsonAddress.address.organization,
    streetNumber: jsonAddress.address.street_no,
    street1: jsonAddress.address.address_line_1,
    street2: jsonAddress.address.address_line_2,
    city: jsonAddress.address.city_locality,
    state: jsonAddress.address.state_province,
    zip: jsonAddress.address.postal_code,
    country: countries[jsonAddress.address.country_code as keyof typeof countries],
    phone: jsonAddress.address.phone,
    email: jsonAddress.address.email,
    isResidential: jsonAddress.address.address_type === AddressTypeV2.RESIDENTIAL,
  };
}

/**
 * This is to map the BaseAddress to JSONAddressBookAddress
 * @param address
 * @returns
 */
function toAddressBookJSON(address: BaseAddress): JSONAddressBookAddress {
  return {
    id: address.objectId!,
    address: {
      name: address.name,
      organization: address.company,
      email: address.email,
      phone: address.phone || '',
      address_line_1: address.street1,
      address_line_2: address.street2,
      street_no: address.streetNumber,
      city_locality: address.city,
      state_province: address.state,
      postal_code: address.zip,
      country_code: address.country?.iso2 || countries.US.iso2,
      // If it's not residential then it's unknown
      address_type: address.isResidential ? AddressTypeV2.RESIDENTIAL : AddressTypeV2.UNKNOWN,
    },
    // This is okay to be empty because we don't use these to update the value
    // These values are updated by the backend irrespective of what we send
    updated_at: '',
    created_at: '',
  };
}

function toJSON(address: Address): JSONAddress {
  return {
    id: address.id,
    object_id: address.objectId,
    object_state: address.objectState,
    object_purpose: address.objectPurpose,
    object_source: address.objectSource,
    object_created: address.objectCreated?.toISOString(),
    object_updated: address.objectUpdated?.toISOString(),
    object_owner: address.objectOwner,
    name: address.name,
    company: address.company,
    street_no: address.streetNumber,
    street1: address.street1,
    street2: address.street2,
    street3: address.street3,
    city: address.city,
    state: address.state,
    zip: address.zip,
    // TODO: I dunno if empty string is correct
    country: address.country ? address.country.iso2 : '',
    longitude: address.longitude,
    latitude: address.latitude,
    phone: address.phone ? address.phone : '',
    email: address.email,
    is_residential: address.isResidential,
    ip: address.ip,
    messages: address.messages,
    metadata: address.metadata,
    test: address.test,
  };
}

function addressToJSON(address?: Address): JSONBaseAddress | undefined {
  if (!address) return undefined;

  return {
    name: address.name,
    company: address.company,
    email: address.email,
    phone: address.phone ? address.phone : '',
    street1: address.street1,
    street2: address.street2,
    street_no: address.streetNumber,
    city: address.city,
    state: address.state,
    zip: address.zip,
    country: address.country ? address.country.iso2 : '',
  };
}

export const fromWebAddress = (webAddress: WebAddress): Address => {
  const country: Country | undefined = webAddress.country
    ? countries[webAddress.country.toUpperCase()]
    : undefined;
  return {
    name: webAddress.name,
    company: webAddress.company,
    email: webAddress.email,
    phone: webAddress.phone,
    street1: webAddress.street1,
    street2: webAddress.street2,
    street3: webAddress.street3,
    streetNumber: webAddress.street_no,
    city: webAddress.city,
    state: webAddress.state,
    zip: webAddress.zip,
    isResidential: webAddress.is_residential,
    objectId: webAddress.object_id,
    objectState: webAddress.object_state,
    objectPurpose: webAddress.object_purpose,
    objectSource: webAddress.object_source,
    objectCreated: moment(webAddress.object_created).toDate(),
    objectUpdated: moment(webAddress.object_updated).toDate(),
    longitude: webAddress.longitude,
    latitude: webAddress.latitude,
    ip: webAddress.ip,
    metadata: webAddress.metadata,
    country,

    //nulls
    objectOwner: '',
    messages: [],
    test: false,
    id: -1,
  };
};

function countryUsesStreetNumberSuffix(country: Maybe<Country>) {
  if (!country) return false;

  return ['DE', 'AT', 'CH'].includes(country.iso2);
}

function sanitizeAddress(address: BaseAddress) {
  return !countryUsesStreetNumberSuffix(address.country)
    ? {
        ...address,
        streetNumber: null,
      }
    : address;
}

function getStreetLine(address: BaseAddress) {
  return countryUsesStreetNumberSuffix(address.country)
    ? `${address.streetNumber} ${address.street1}`
    : `${address.street1}`;
}

function getRequiredAddressFieldsByCountry(country: Maybe<Country>): Maybe<string[]> {
  if (!country) return;

  const requireState = isStateMandatory(country);
  const requireStreetNumSuffix = countryUsesStreetNumberSuffix(country);

  if (requireState) {
    return ADDRESS_INPUT_FIELDS.requireStateField;
  }

  if (requireStreetNumSuffix) {
    return ADDRESS_INPUT_FIELDS.requireStreetNumberSuffix;
  }

  return ADDRESS_INPUT_FIELDS.minRequireFields;
}

function isAddressFieldsComplete(address: BaseAddress, requiredFields: Maybe<string[]>): boolean {
  if (!Array.isArray(requiredFields)) return false;
  return requiredFields.every((field) => address[field as keyof BaseAddress]);
}

function isStateMandatory(country: Maybe<Country>): boolean {
  return (
    isEqual(country, countries.US) ||
    isEqual(country, countries.CA) ||
    isEqual(country, countries.IE)
  );
}

function isAddressIncomplete(address: APIAddress & BaseAddress): boolean {
  const missingMinimumRequiredFields =
    !address.country || !address.city || !address.street1 || !address.name;
  if (missingMinimumRequiredFields) return true;

  // TODO: update this when we support more store imported addresses
  const isShopify =
    address.hasOwnProperty('storePlatformNames') &&
    address.storePlatformNames?.includes(StoreName.Shopify);
  const missingState = isStateMandatory(address.country) && !address.state;
  const missingStreetNumber =
    !isShopify && countryUsesStreetNumberSuffix(address.country) && !address.streetNumber;
  const missingZip = !address.zip && countryRequiresZip(<Country>address.country);

  return missingState || missingStreetNumber || missingZip;
}

function isBaseAddress(address: number | BaseAddress): address is BaseAddress {
  return (<BaseAddress>address).street1 !== undefined;
}

function areAddressesEqual(
  firstAddress: Maybe<BaseAddress>,
  secondAddress: Maybe<BaseAddress>,
): boolean {
  if (!firstAddress || !secondAddress) {
    return firstAddress === secondAddress;
  }

  return (
    firstAddress.street1 === secondAddress.street1 &&
    firstAddress.street2 === secondAddress.street2 &&
    firstAddress.streetNumber === secondAddress.streetNumber &&
    firstAddress.zip === secondAddress.zip
  );
}

function areAddressesFullyEqual(
  firstAddress: Maybe<BaseAddress>,
  secondAddress: Maybe<BaseAddress>,
): boolean {
  if (!firstAddress || !secondAddress) {
    return firstAddress === secondAddress;
  }

  if (!areAddressesEqual(firstAddress, secondAddress)) return false;

  return (
    firstAddress.name === secondAddress.name &&
    firstAddress.company === secondAddress.company &&
    firstAddress.phone === secondAddress.phone &&
    firstAddress.email === secondAddress.email &&
    firstAddress.city === secondAddress.city &&
    firstAddress.state === secondAddress.state &&
    firstAddress.country?.iso2 === secondAddress.country?.iso2
  );
}

function areAllAddressesEqual(addresses: BaseAddress[]): boolean {
  if (addresses.length < 2) return false;
  for (let i = 0; i < addresses.length - 1; i += 1) {
    const j = i + 1;
    if (!areAddressesEqual(addresses[i], addresses[j])) {
      return false;
    }
  }
  return true;
}

function areAllAddressesFullyEqual(addresses: BaseAddress[]): boolean {
  if (addresses.length < 2) return false;
  for (let i = 0; i < addresses.length - 1; i += 1) {
    if (!areAddressesFullyEqual(addresses[i], addresses[i + 1])) {
      return false;
    }
  }

  return true;
}

function getCountryStates(country: Country): State[] {
  const countryStates = states[country.iso2];

  if (countryStates) {
    return Object.values(countryStates);
  }

  return [];
}

function getStateByCountry(state: string, country: Country): State {
  const countryStates = states[country.iso2];

  if (countryStates) {
    return countryStates[state];
  }

  return {
    name: state,
    iso: state,
  };
}

function countryUsesStateList(country: Country): boolean {
  return (
    country.iso2 === countries.US.iso2 ||
    country.iso2 === countries.CA.iso2 ||
    country.iso2 === countries.AU.iso2
  );
}

function getStateOptions(states: State[]): Option<string>[] {
  return states.map((state) => {
    return { value: state.iso, label: state.name };
  });
}

function getCountryOptions(suggestedCountryCode?: string, icon = false): Option<string>[] {
  const options = Object.values(countries).map((c) => formatCountryToOption(c, icon)!);

  if (!suggestedCountryCode) {
    return options;
  }
  const defaultCountry: Country | undefined =
    countries[suggestedCountryCode as keyof typeof countries];
  if (!defaultCountry) {
    return options;
  }

  const defaultCountryAsOption = formatCountryToOption(defaultCountry, icon);

  return options.sort((a) => {
    if (a.value === defaultCountryAsOption.value) {
      return -(options.length + 1);
    }

    return 0;
  });
}

// By taking the country options as an input, we're able to avoid creating a new array on every key press
export function countryMatchingInput(
  countryOptions: Option<string>[],
  inputCountryName: string,
): Country | undefined {
  const match = countryOptions.find(
    (c) => (c.label as string).toLowerCase() === inputCountryName.toLowerCase(),
  );
  if (!match) {
    return;
  }
  return countries[match.value as CountryIso2];
}

export function inputValueMatchCountry(v: string): Country | undefined {
  return Object.values(countries).find((c) => {
    if (c.name.toLowerCase() === v.toLowerCase() || c.iso2.toLowerCase() === v.toLowerCase()) {
      return c;
    }
    return;
  });
}

export function inputValueMatchState(
  v: string,
  countryStates: { [key: string]: State },
): State | undefined {
  return Object.values(countryStates).find((s) => {
    if (s.name.toLowerCase() === v.toLowerCase() || s.iso.toLowerCase() === v.toLowerCase()) {
      return s;
    }
    return;
  });
}

function countryRequiresZip(country: Country): boolean {
  return !countriesWithoutZip.find((c) => c.iso2 === country.iso2);
}

function shortUSZipCode(zip: string): string {
  return zip.substring(0, 5);
}

function isMilitaryAddress(address: BaseAddress): boolean {
  return (
    (!!address.state &&
      Object.values(usMilitaryStates)
        .map((state) => state.iso)
        .includes(address.state.toUpperCase())) ||
    (!!address.city && usMilitaryCities.includes(address.city.toUpperCase()))
  );
}

function isUSTerritory(address: BaseAddress): boolean {
  return (
    !!address.state &&
    Object.values(usTerritoryStates)
      .map((state) => state.iso)
      .includes(address.state.toUpperCase())
  );
}

function isUSorMilitaryTerritory(territory: string) {
  return Object.values({ ...usTerritoryStates, ...usMilitaryStates })
    .map((state) => state.iso)
    .includes(territory.toUpperCase());
}

// TODO: Properly type this Order, Maybe<BatchEntry>, returned are Maybe<Address> so consumers need to be updated
function getAddresses(
  order: Order,
  batchEntry?: BatchEntryExtended | null,
): { addressTo: Address; addressFrom: Address } {
  const addressFrom = batchEntry?.addressFrom || order.addressFrom;
  const addressTo = batchEntry?.addressTo || order.toAddress;

  return {
    addressTo: addressTo!,
    addressFrom: addressFrom!,
  };
}

// This is a typesafe version of the function above. Please use this and refactor existing code when possible.
function getAddressesTypeSafe(
  order?: Order,
  batchEntry?: BatchEntryExtended,
): { addressTo: Address | undefined; addressFrom: Address | undefined } {
  const addressFrom = batchEntry?.addressFrom || order?.addressFrom;
  const addressTo = batchEntry?.addressTo || order?.toAddress;

  return {
    addressTo: addressTo || undefined,
    addressFrom: addressFrom || undefined,
  };
}

// USPS
function isUntrackableInternationalDestination(address: BaseAddress): boolean {
  return (
    !!address.country &&
    !uspsTrackingCountryIso2Whitelist.includes(address.country.iso2) &&
    !uspsTrackingCountryAdditionalWhitelist.includes(address.country.iso2 as string)
  );
}

function isAddressCrossBorder(senderAddress?: Address, recipientAddress?: Address) {
  if (!senderAddress || !recipientAddress) {
    return false;
  }
  return (
    !!senderAddress.country &&
    !!recipientAddress.country &&
    senderAddress.country.iso2 !== recipientAddress.country.iso2
  );
}

export default Address;
export {
  fromJSON,
  JSONAddress,
  Country,
  State,
  JSONBaseAddress,
  DisplayInvoiceAddress,
  BaseAddress,
  countryUsesStreetNumberSuffix,
  getStreetLine,
  isAddressIncomplete,
  isBaseAddress,
  areAddressesEqual,
  areAddressesFullyEqual,
  areAllAddressesEqual,
  areAllAddressesFullyEqual,
  getCountryStates,
  countryUsesStateList,
  getStateOptions,
  getCountryOptions,
  getStateByCountry,
  isStateMandatory,
  countryRequiresZip,
  toJSON,
  addressToJSON,
  shortUSZipCode,
  isMilitaryAddress,
  isUSTerritory,
  isUntrackableInternationalDestination,
  sanitizeAddress,
  AddressType,
  AddressToType,
  AddressSenderType,
  AddressReturnType,
  isUSorMilitaryTerritory,
  getAddresses,
  getAddressesTypeSafe,
  isAddressFieldsComplete,
  getRequiredAddressFieldsByCountry,
  AddressIt,
  PastedAddress,
  isAddressCrossBorder,
  fromAddressBookJSON,
  toAddressBookJSON,
  JSONAddressBookAddress,
};
